import { cn } from '@/lib/cn';
import { useToast, type ToastMessage, type ToastVariant } from '@/hooks/useToast';

const variantAccent: Record<ToastVariant, string> = {
  success: 'border-l-emerald-500',
  error: 'border-l-red-500',
  info: 'border-l-[var(--accent)]',
};

export interface ToastItemProps {
  toast: ToastMessage;
  onDismiss: (id: string) => void;
}

export function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const isError = toast.variant === 'error';
  return (
    <div
      className={cn(
        'rounded-lg border border-[var(--border-visible)] border-l-4 px-4 py-3 shadow-lg',
        variantAccent[toast.variant],
      )}
      style={{ background: 'var(--bg-toast)' }}
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-medium text-[var(--text-primary)]">{toast.title}</p>
          {toast.description ? (
            <p className="mt-1 text-sm text-[var(--text-secondary)]">{toast.description}</p>
          ) : null}
        </div>
        <button
          type="button"
          className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          onClick={() => onDismiss(toast.id)}
          aria-label="Dismiss notification"
        >
          ×
        </button>
      </div>
    </div>
  );
}

export function ToastViewport({ className }: { className?: string }) {
  const { toasts, dismissToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div
      className={cn(
        'fixed right-4 top-4 z-[200] flex max-w-sm flex-col gap-2',
        className,
      )}
      aria-live="polite"
      aria-relevant="additions"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>
  );
}
